// The mentor-card look shared by /mentors (mentors-browser.js) and the partner
// programme pages (src/revamp/pages/partner-mentoring/*). Everything here was
// extracted verbatim from mentors-browser.js so all mentor grids stay identical —
// edit the card or the profile dialog HERE and every page follows.
import React, { useLayoutEffect, useRef, useState } from "react";
import Image from "../../components/image";
import { Icon } from "../../components/icon";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { LinkedInLogo } from "../../components/linkedin-logo";
import { cn } from "../../lib/utils";
import config from "../../../config";

/* ── URL <-> control constants (identical on every mentors page) ───────────── */
export const SORT_OPTIONS = [
  { label: "Mentor (A-Z)", direction: "asc" },
  { label: "Mentor (Z-A)", direction: "desc" },
];
export const AVAILABILITY_OPTIONS = [
  { label: "All Mentors", value: "all" },
  { label: "Available", value: "yes" },
  { label: "Unavailable", value: "no" },
];
// Design label -> API flag. Order matches lib/content.js mentorSpecialFilters.
export const SPECIAL_PARAMS = ["mf_mental_health", "mf_certified_coach", "lgbtq_discussion"];
export const SPECIAL_DESC = {
  mf_mental_health: config.mf_mental_health,
  mf_certified_coach: config.mf_certified_coach,
  lgbtq_discussion: config.LGBTQ_discussion,
};
export const SPECIAL_ICON = { mf_mental_health: "lotus", mf_certified_coach: "coaching", lgbtq_discussion: "inclusion" };
// Short names for the special-filter chips (full text stays in SPECIAL_DESC).
export const SPECIAL_LABEL = {
  mf_mental_health: "Mental wellness",
  mf_certified_coach: "Certified coach",
  lgbtq_discussion: "LGBTQ+ inclusive",
};
export const PLACEHOLDER_IMG = (process.env.REACT_APP_STATIC_IMAGE_PATH || "") + "mentor_women_two.png";
export const LOGIN_PROMPT =
  "Thank you for browsing vLookUp! Help us create a meaningful mentoring engagement for you by registering in one simple step, or login if you already have an account.";

/* ── API record -> card model ──────────────────────────────────────────────── */
export const isAvailable = (m) =>
  Number(m.is_reached_limit) === 0 && Number(m.user_availability_check?.availability ?? m.user_availability) === 1;

export const splitList = (s) => (s ? String(s).split(",").map((x) => x.trim()).filter(Boolean) : []);

export function fromApi(m) {
  return {
    id: m.id,
    name: m.full_name,
    role: m.position || "",
    company: m.organization && m.organization !== "undefined" ? m.organization : "",
    country: m.country || "",
    flagSrc: m.country_image_path || "",
    domain: m.main_area || "",
    domains: splitList(m.all_main_area),
    subAreas: splitList(m.all_sub_area).slice(0, 6),
    available: isAvailable(m),
    img: m.image || PLACEHOLDER_IMG,
    bio: m.about || "",
    linkedin: m.linkedin || "",
    language: m.language_privacy === false ? "" : m.language || "",
    openTo: Array.isArray(m.experience_levels) ? m.experience_levels.map((l) => l.title).filter(Boolean) : [],
    special: SPECIAL_PARAMS.filter((p) => Number(m[p]) === 1),
    engagements: (parseInt(m.total_completed_mentees, 10) || 0) + (parseInt(m.total_ongoing_mentees, 10) || 0),
    raw: m,
  };
}

/* ── The card ──────────────────────────────────────────────────────────────── */
export function MentorCard({ mentor: m, cta, onOpen }) {
  return (
    <article
      className="group flex flex-col overflow-hidden rounded-3xl bg-card text-left shadow-sm ring-1 ring-brand-navy/5 transition-all hover:-translate-y-1.5 hover:shadow-xl"
    >
      <div className="relative aspect-square overflow-hidden">
        <Image
          src={m.img}
          alt=""
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={cn(
            "pointer-events-none absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1.5 text-[11px] font-bold shadow-sm",
            m.available ? "text-brand-green" : "text-muted-foreground"
          )}
        >
          <span className={cn("size-2 rounded-full", m.available ? "bg-brand-green" : "bg-muted-foreground/50")} />
          {m.available ? "Available" : "Unavailable"}
        </span>
        <Flag mentor={m} className="pointer-events-none absolute bottom-3 left-3" />
        <button
          type="button"
          onClick={() => onOpen(m)}
          className="absolute inset-0 z-[6]"
          aria-label={`View ${m.name}'s profile`}
        />
        {/* Mobile: keep the visible pill; photo tap (button above) is the real target. */}
        <button
          type="button"
          onClick={() => onOpen(m)}
          className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-card px-3 py-1.5 text-[11px] font-bold text-brand-navy shadow-sm sm:hidden"
        >
          View profile
        </button>
        {/* Desktop hover actions sit above the photo hit-target. */}
        <div
          className="pointer-events-none absolute inset-0 z-10 hidden flex-col items-stretch justify-center gap-2.5 bg-brand-blue/40 px-6 opacity-0 transition-opacity duration-300 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 sm:flex"
          onClick={() => onOpen(m)}
        >
          {cta && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (typeof cta.onClick === "function") {
                  cta.onClick(e);
                }
              }}
              className="rounded-md border border-white/90 bg-white/15 px-3 py-2.5 text-center text-[13px] font-bold text-white backdrop-blur-sm transition-colors hover:border-brand-red hover:bg-brand-red"
            >
              {cta.label}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(m);
            }}
            className="rounded-md border border-white/90 bg-white/15 px-3 py-2.5 text-[13px] font-bold text-white backdrop-blur-sm transition-colors hover:border-brand-red hover:bg-brand-red"
          >
            Read More
          </button>
        </div>
      </div>
      <button type="button" onClick={() => onOpen(m)} className="flex flex-1 flex-col p-5 text-left">
        <h3 className="font-display text-xl font-semibold text-heading">{m.name}</h3>
        {m.role && <p className="mt-1 text-sm font-medium text-foreground/80">{m.role}</p>}
        {m.company && <p className="text-sm text-muted-foreground">{m.company}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          {/* One neutral chip style for every interest area (QA row 46) */}
          {m.domain && (
            <span className="rounded-full border border-brand-navy/15 bg-cream px-2.5 py-1 text-[11px] font-bold text-brand-navy/70">{m.domain}</span>
          )}
          {m.special.map((p) => (
            <SpecialTooltipChip key={p} param={p} />
          ))}
        </div>
        {m.openTo.length > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-navy/50">Open to</span>
            {m.openTo.map((lv) => (
              <span key={lv} className="rounded-full border border-brand-navy/15 bg-cream px-2.5 py-1 text-[10px] font-bold text-brand-navy/70">{lv}</span>
            ))}
          </div>
        )}
        {m.language && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground" aria-label={`Mentoring languages: ${m.language}`}>
            <LanguagesGlyph className="size-3.5 shrink-0 text-brand-blue" />
            {m.language}
          </p>
        )}
      </button>
    </article>
  );
}

/* ── The "Read More" profile dialog ────────────────────────────────────────── */
export function MentorDialog({ open, onOpenChange, mentor: selected, cta }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] w-full max-w-[calc(100%-2rem)] gap-0 overflow-y-auto rounded-3xl p-0 ring-1 ring-brand-navy/10 sm:max-w-2xl">
        {/* min-w-0 / minmax(0,1fr): grid items and tracks default to a
            min-content floor, so one long word in a bio or interest area
            widened the panel and DialogContent scrolled sideways. */}
        {selected && (
          <div className="grid min-w-0 break-words sm:grid-cols-[240px_minmax(0,1fr)]">
            <div className="min-w-0 bg-cream-deep p-7">
              <div className="relative aspect-square overflow-hidden rounded-2xl">
                <Image src={selected.img} alt={selected.name} fill sizes="240px" className="object-cover object-top" />
                <Flag mentor={selected} className="absolute bottom-2 left-2" />
              </div>
              <DialogTitle className="mt-4 font-display text-xl font-semibold text-heading">{selected.name}</DialogTitle>
              {selected.role && <p className="mt-1 text-sm font-medium text-foreground/80">{selected.role}</p>}
              <p className="text-sm text-muted-foreground">{selected.company || "N/A"}</p>
              <span
                className={cn(
                  "mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold",
                  selected.available ? "bg-brand-green/10 text-brand-green" : "bg-muted text-muted-foreground"
                )}
              >
                <span className={cn("size-2 rounded-full", selected.available ? "bg-brand-green" : "bg-muted-foreground/50")} />
                {selected.available ? "Available" : "Unavailable"}
              </span>
              {(selected.special.length > 0 || selected.engagements > 0) && (
                <ul className="mt-4 space-y-2">
                  {selected.special.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-xs leading-snug text-brand-navy/75">
                      <Icon name={SPECIAL_ICON[p]} size={28} className="size-7 shrink-0" />
                      {SPECIAL_DESC[p]}
                    </li>
                  ))}
                  {/* Old details modal showed this stat with an icon (QA row 17) */}
                  {selected.engagements > 0 && (
                    <li className="flex items-center gap-2 text-xs font-semibold text-brand-navy/75">
                      <Icon name="handshake" size={28} className="size-7 shrink-0" />
                      No. of mentoring engagements: {selected.engagements}
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div className="flex min-w-0 flex-col p-7">
              <DialogDescription className="sr-only">Mentor profile for {selected.name}</DialogDescription>
              {/* One neutral chip style for every interest area (QA row 46) */}
              <div className="flex flex-wrap items-center gap-1.5">
                {[...new Set([...selected.domains, ...selected.subAreas])].map((d) => (
                  <span key={d} className="inline-flex w-fit max-w-full rounded-full border border-brand-navy/15 bg-cream px-2.5 py-1 text-[11px] font-bold text-brand-navy/70">{d}</span>
                ))}
              </div>
              {selected.openTo.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-navy/50">Open to</span>
                  {selected.openTo.map((lv) => (
                    <span key={lv} className="rounded-full border border-brand-navy/15 bg-cream px-2.5 py-1 text-[10px] font-bold text-brand-navy/70">{lv}</span>
                  ))}
                </div>
              )}
              {selected.language && (
                <p className="mt-2.5 flex items-center gap-1.5 text-xs text-muted-foreground" aria-label={`Mentoring languages: ${selected.language}`}>
                  <LanguagesGlyph className="size-3.5 shrink-0 text-brand-blue" />
                  {selected.language}
                </p>
              )}
              <p className="mt-4 flex-1 whitespace-pre-line text-[15px] leading-relaxed text-muted-foreground">{selected.bio}</p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {cta && (
                  <button
                    type="button"
                    onClick={cta.onClick}
                    className="h-auto rounded-full bg-brand-red px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-red-dark"
                  >
                    {cta.label}
                  </button>
                )}
                {selected.linkedin && (
                  <a
                    href={selected.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-navy/15 px-4 py-2.5 text-sm font-bold text-[#0A66C2] transition-colors hover:border-[#0A66C2]"
                  >
                    <LinkedInLogo className="size-4" />
                    LinkedIn Profile
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Small shared pieces ───────────────────────────────────────────────────── */
// Compact page list: 1 … c-1 c c+1 … N
export function pageWindow(current, total) {
  const pages = new Set([1, total, current - 1, current, current + 1].filter((p) => p >= 1 && p <= total));
  const list = [...pages].sort((a, b) => a - b);
  const out = [];
  list.forEach((p, i) => {
    if (i > 0 && p - list[i - 1] > 1) out.push("…");
    out.push(p);
  });
  return out;
}

export function Flag({ mentor, className }) {
  if (mentor.flagSrc) {
    return (
      <span className={cn("flex size-9 items-center justify-center rounded-full bg-card shadow-sm", className)} title={mentor.country}>
        <img src={mentor.flagSrc} alt={mentor.country || ""} className="size-5 rounded-sm object-cover" loading="lazy" />
      </span>
    );
  }
  if (mentor.flag) {
    return <span className={cn("flex size-9 items-center justify-center rounded-full bg-card text-lg shadow-sm", className)}>{mentor.flag}</span>;
  }
  return null;
}

export function PagerButton({ children, active, disabled, onClick, ...props }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex size-10 items-center justify-center rounded-full border text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        active ? "border-brand-red bg-brand-red text-white" : "border-brand-navy/15 bg-card text-brand-navy/70 hover:border-brand-red hover:text-brand-red"
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function FilterHeading({ children }) {
  return <div className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-brand-navy/60">{children}</div>;
}

export function Rule() {
  return <div className="my-3 h-px bg-brand-navy/10" />;
}

export function FilterCheck({ label, icon, checked, onChange }) {
  return (
    <label className="group flex cursor-pointer items-center gap-2.5 py-1">
      <input type="checkbox" checked={checked} onChange={onChange} className="size-[15px] shrink-0 accent-brand-red" />
      {icon && <Icon name={icon} size={36} className="size-9 shrink-0" />}
      <span className={cn("text-[13.5px] leading-snug transition-colors group-hover:text-brand-red", checked ? "font-semibold text-brand-navy" : "text-brand-navy/75")}>
        {label}
      </span>
    </label>
  );
}

/* ── Special-declaration icons with a styled tooltip (QA row 45.3) ─────────── */
// `title` alone renders the browser default. This tooltip is position:fixed so
// overflow-x-clip / narrow mobile filters cannot clip the last (LGBTQ) chip.
function useAnchoredTip(open) {
  const hostRef = useRef(null);
  const tipRef = useRef(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const host = hostRef.current;
      const tip = tipRef.current;
      if (!host || !tip) return;
      const r = host.getBoundingClientRect();
      const pad = 10;
      const tw = Math.min(tip.offsetWidth || 216, window.innerWidth - pad * 2);
      tip.style.width = `${tw}px`;
      const th = tip.offsetHeight;
      let left = r.left + r.width / 2 - tw / 2;
      left = Math.max(pad, Math.min(left, window.innerWidth - tw - pad));
      let top = r.top - th - 12;
      if (top < pad) top = r.bottom + 10;
      if (top + th > window.innerHeight - pad) {
        top = Math.max(pad, r.top - th - 12);
      }
      tip.style.left = `${left}px`;
      tip.style.top = `${top}px`;
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);
  return { hostRef, tipRef };
}

function TipBubble({ text, tipRef, open }) {
  return (
    <span ref={tipRef} role="tooltip" className={cn("special-tip", open && "special-tip--open")}>
      {text}
    </span>
  );
}

// Read-only chip used on the mentor card. Not focusable: it sits inside the
// card's "open profile" button, where the dialog shows the full text anyway.
export function SpecialTooltipChip({ param, className }) {
  const [open, setOpen] = useState(false);
  const { hostRef, tipRef } = useAnchoredTip(open);
  return (
    <span
      ref={hostRef}
      aria-label={SPECIAL_DESC[param]}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className={cn("special-tip-host group/tip relative inline-flex size-7 shrink-0 cursor-help items-center justify-center rounded-full bg-cream-deep", className)}
    >
      <Icon name={SPECIAL_ICON[param]} size={20} className="size-5 object-contain" />
      <TipBubble text={SPECIAL_DESC[param]} tipRef={tipRef} open={open} />
    </span>
  );
}

// Toggle chip used by the filter sidebars (QA row 45.1/45.3): icon-only with
// the description in the tooltip, visible near the top of the filters.
export function SpecialFilterChip({ param, checked, onChange }) {
  const [open, setOpen] = useState(false);
  const { hostRef, tipRef } = useAnchoredTip(open);
  return (
    <button
      ref={hostRef}
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={SPECIAL_LABEL[param]}
      onClick={onChange}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      className={cn(
        "special-tip-host group/tip relative z-10 flex size-11 shrink-0 items-center justify-center rounded-2xl transition-all",
        checked
          ? "bg-brand-blue/10 ring-2 ring-brand-blue"
          : "bg-cream ring-1 ring-brand-navy/10 hover:ring-brand-blue/60"
      )}
    >
      <Icon name={SPECIAL_ICON[param]} size={26} className="size-[26px] object-contain" />
      {checked && (
        <span aria-hidden className="absolute -right-1.5 -top-1.5 z-0 flex size-4 items-center justify-center rounded-full bg-brand-blue text-[9px] font-bold leading-none text-white">
          ✓
        </span>
      )}
      <TipBubble text={SPECIAL_DESC[param]} tipRef={tipRef} open={open} />
    </button>
  );
}

// lucide "languages" glyph, inlined (not part of the shared generated icon set).
export function LanguagesGlyph({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="m5 8 6 6" />
      <path d="m4 14 6-6 2-3" />
      <path d="M2 5h12" />
      <path d="M7 2h1" />
      <path d="m22 22-5-10-5 10" />
      <path d="M14 18h6" />
    </svg>
  );
}
